import { useFlags } from '@atlaskit/flag';
import Heading from '@atlaskit/heading';
import LinkIcon from '@atlaskit/icon/glyph/link';
import { Box, Inline, Stack, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import { type ReactNode, useState } from 'react';

// Constants
const SCROLL_MARGIN_TOP = '100px';

// Styles
const containerStyles = xcss({
    maxWidth: '800px',
    margin: 'auto',
    padding: 'space.400',
});

const sectionStyles = xcss({
    marginTop: 'space.100',
});

const listStyles = xcss({
    paddingLeft: 'space.400',
    marginTop: 'space.200',
});

const headingContainerStyles = xcss({
    position: 'relative',
});

const linkButtonStyles = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
    display: 'flex',
    alignItems: 'center',
    opacity: 0.6,
    transition: 'opacity 0.2s',
} as const;

// Types
interface HeadingWithAnchorProps {
    id: string;
    size: 'large' | 'medium' | 'small' | 'xlarge' | 'xxlarge' | 'xsmall' | 'xxsmall';
    children: ReactNode;
}

interface SectionProps {
    id: string;
    title: string;
    children: ReactNode;
}

// Components
const HeadingWithAnchor = ({ id, size, children }: HeadingWithAnchorProps) => {
    const [isHovered, setIsHovered] = useState(false);
    const { showFlag } = useFlags();

    const handleCopyLink = () => {
        const url = `${window.location.origin}${window.location.pathname}#${id}`;
        navigator.clipboard.writeText(url).then(() => {
            showFlag({
                appearance: 'success',
                title: 'Link copied to clipboard',
                isAutoDismiss: true,
            });
        });
    };

    return (
        <Box
            xcss={headingContainerStyles}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <Inline space="space.100" alignBlock="center">
                <Heading size={size}>{children}</Heading>
                {isHovered && (
                    <button
                        onClick={handleCopyLink}
                        style={linkButtonStyles}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.opacity = '1';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.opacity = '0.6';
                        }}
                        title="Copy link to section"
                    >
                        <LinkIcon label="Copy link" size="small" />
                    </button>
                )}
            </Inline>
        </Box>
    );
};

const Section = ({ id, title, children }: SectionProps) => (
    <Box xcss={sectionStyles} id={id} style={{ scrollMarginTop: SCROLL_MARGIN_TOP }}>
        <HeadingWithAnchor size="medium" id={id}>
            {title}
        </HeadingWithAnchor>
        {children}
    </Box>
);

export const DatabricksHelp = () => {
    return (
        <Box xcss={containerStyles}>
            <Stack space="space.400">
                <Box>
                    <Heading size="large">Databricks Query Runner - Help</Heading>
                </Box>

                <Box>
                    <SectionMessage appearance="warning">
                        This page might eventually be moved to support.atlassian.com
                    </SectionMessage>
                </Box>

                <Section id="what-is-it" title="What is it?">
                    <Box paddingBlockStart="space.200">
                        <strong>Need Databricks Data? Think Databricks Agent!</strong>
                    </Box>
                    <Box paddingBlockStart="space.200">
                        Atlassian&apos;s{' '}
                        <a
                            href="https://marketplace.atlassian.com/apps/1237409/databricks-rovo-agent?hosting=cloud&tab=overview"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            Databricks Query Runner (AKA Databricks Agent)
                        </a>{' '}
                        is your new agent for instant, AI-powered insights, accessed within Atlassian Rovo. This
                        agent is powered by Databricks&apos;s AI/BI Genie, a conversational analytics tool that
                        empowers users to ask natural language questions and receive instant information and
                        dashboards from their data. Built on compound AI and integrated deeply with the Databricks
                        platform, Genie enables real-time, self-service analytics. Now, anyone on your team can
                        query structured data in Databricks to get faster, smarter, and more democratized access to
                        data-driven insights—no SQL or data science expertise required.
                    </Box>
                </Section>

                <Section id="how-to-configure" title="How to configure this agent?">
                    <Box paddingBlockStart="space.200">
                        <SectionMessage appearance="warning">
                            <p>
                                Automatic data deletion is not yet configured; work is being done to hook this data to
                                the typical tenant lifecycle, which will ensure data is cleaned up correctly.
                            </p>
                            <p>
                                Until then, any customer who would like their data to be cleaned up, please feel free to
                                reach out to us directly.
                            </p>
                        </SectionMessage>
                    </Box>

                    <Box paddingBlockStart="space.200">
                        Ensure you are a site admin of the cloud site you want to configure, or contact the site admin
                        with these instructions.
                    </Box>

                    <Box xcss={listStyles} as="ol">
                        <li>
                            Visit{' '}
                            <a
                                href="https://rovo-integrations.atlassian.com/databricks/onboard"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                https://rovo-integrations.atlassian.com/databricks/onboard
                            </a>
                        </li>
                        <li>You will see all cloud sites where you have admin access.</li>
                        <li>
                            Select the cloud site to configure the Databricks connection and provide these details:
                            <Box xcss={listStyles} as="ol" paddingBlockStart="space.100">
                                <li>Databricks host</li>
                                <li>OAuth Client ID</li>
                                <li>OAuth Client secret</li>
                            </Box>
                        </li>
                        <li>
                            Ensure your Databricks host allows{' '}
                            <a
                                href="http://databricks-oauth.atlassian.com/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                http://databricks-oauth.atlassian.com/
                            </a>{' '}
                            in its allow list, if applicable.
                        </li>
                    </Box>
                </Section>

                <Section id="how-to-use" title="How to use this agent?">
                    <Box paddingBlockStart="space.200">As a user,</Box>

                    <Box xcss={listStyles} as="ol">
                        <li>
                            Visit any Atlassian surface that has Rovo integrated, like the Rovo button available in the
                            navigation bar.
                        </li>
                        <li>
                            From the agent list, select &apos;Databricks Query Runner&apos; (AKA &apos;Databricks
                            Agent&apos;) and start your conversation.
                        </li>
                        <li>
                            If this is the first time you are interacting with the agent, please note that you will have
                            to authorize the agent to access your Genie Spaces on your behalf. It will provide you with
                            an authorization URL, which will take you to Atlassian Identity and your Databricks Host for
                            authorization.
                            <Box xcss={listStyles} as="ol" paddingBlockStart="space.100">
                                <li>
                                    After this authorization is provided, the agent will automatically restart your
                                    conversation and then begin indexing your Genie Spaces. It usually takes around 5 to
                                    10 minutes but can take longer in certain cases.
                                </li>
                            </Box>
                        </li>
                    </Box>
                </Section>

                <Section id="what-gets-indexed" title="What gets indexed?">
                    <Box paddingBlockStart="space.200">
                        We only index metadata about the space, not the data the space holds.
                    </Box>
                </Section>

                <Section id="need-support" title="Need support?">
                    <Box paddingBlockStart="space.200">
                        <SectionMessage appearance="information">
                            Please reach out to us for assistance with the Databricks Query Runner.
                        </SectionMessage>
                    </Box>
                </Section>
            </Stack>
        </Box>
    );
};
